<template>
    <header class="header bg-sky-900" v-if="!route.query.print">
        <nav
            class="inner"
            role="navigation"
            >
            <NuxtLink :to="user ? (user.is_admin || (user.projects?.length ?? 0) > 1 ? '/projects' : '/') : (route.query.read ? '' : '/user-not-found')">
                <div class="logo">
                    <img src="/assets/images/raci-logo.png" />
                </div>
            </NuxtLink>

            <div class="menu-setup">
                <NuxtLink :to="user ? '/' : ''" class="text-lg mr-5" :title="project.name + ' ' + getStaticTranslation('tasks')" v-if="project">
                    <div class="rounded border-white border px-3 py-1" v-if="project">{{ project.name }}</div>
                </NuxtLink>
                <NuxtLink to="/setup" :title="project.name + ' ' + getStaticTranslation('settings')" v-if="project && isProjectAdmin">
                    <span class="pi pi-cog" />
                </NuxtLink>
                <NuxtLink to="/projects" :title="getStaticTranslation('projects_list')" class="ml-5" v-if="user && (user.is_admin || (user.projects?.length ?? 0) > 1)">
                    <span class="pi pi-folder" />
                </NuxtLink>

                <div class="ml-4 relative inline-block" v-if="user">
                    <div class="cursor-pointer text-sm px-2 py-1 hover:bg-white hover:text-sky-900 transition-colors"
                         @click="toggleLanguageMenu" ref="languageToggle">
                        {{ viewLanguage }}
                        <i class="pi pi-angle-down ml-1 text-xs"></i>
                    </div>
                </div>

                <div class="inline ml-4 p-1.5 border rounded-full w-4 h-4 text-sm cursor-pointer" @click="toggleUser" v-if="user">{{ user.firstname[0] + user.lastname[0] }}</div>
            </div>
        </nav>
        <Popover ref="op_user">
            <div>
                <div class="text-lg px-4 pt-2">{{ user.name }}</div>
                <div class="text-xs border-b-primary-300 border-b px-4 pb-2">{{ user.email }}</div>
                <ul class="p-4">
                    <li>
                        <NuxtLink :to="config.public.api_endpoint + '/auth/logout'">
                            <span class="pi pi-sign-out mr-2" /> {{ getStaticTranslation('logout') }}
                        </NuxtLink>
                    </li>
                </ul>
            </div>
        </Popover>

        <Popover ref="op_language">
            <div class="p-2">
                <div
                    v-for="lang in languageOptions"
                    :key="lang.value"
                    class="cursor-pointer px-3 py-2 hover:bg-gray-100 rounded transition-colors"
                    @click="selectLanguage(lang.value)"
                    :class="{ 'bg-blue-100 font-medium': viewLanguage === lang.value }"
                >
                    {{ lang.label }}
                </div>
            </div>
        </Popover>
    </header>
</template>

<script>
import mainMixin from '~/mixins/main.mixin.js';
import translationMixin from '~/mixins/translation.mixin.js';
import { useTranslations } from '~/composables/useTranslations';

export default{
    mixins: [mainMixin, translationMixin],
    data() {
        return {
            currentLanguage: 'NL',
            viewLanguage: 'NL', // Language for viewing content (temporary)
            languageOptions: [
                { label: 'Nederlands', value: 'NL' },
                { label: 'Français', value: 'FR' },
                { label: 'English', value: 'EN' }
            ]
        }
    },
    methods: {
        async init() {
            useHead({
                title: 'RACI app',
            })
            if(!this.route.query.print && !this.route.query.read){
                if(!this.user){
                    if(this.route.name != 'user-not-found'){
                        this.client('/sanctum/csrf-cookie').then(response => {
                            this.client('/api/user').then(response => {
                                this.user = response;
                                this.init();
                            })
                            .catch((e) => {
                                window.location.href = this.config.public.api_endpoint + '/auth/redirect';
                            });
                        })
                        .catch((e) => {
                            window.location.href = this.config.public.api_endpoint + '/auth/redirect';
                        });

                    }
                }else if(!this.project){
                    if(this.user.is_admin || this.user.projects.length > 1){
                        this.$router.push('/projects');
                    }else{
                        if(this.user.projects.length == 1){
                            this.project = await this.client('/api/projects/' + this.user.projects[0].id);
                            this.$router.push('/');
                        }
                    }

                }else{
                    useHead({
                        title: 'RACI app - ' + this.project.name,
                    })
                }
            } else if (this.route.query.token) {
                this.client('/read/' + this.route.query.read + '/' + this.route.query.token).then(response => {
                    this.user = null;
                    this.project = response;
                })
            }
        },
        toggleUser(event) {
            this.$refs.op_user.toggle(event);
        },
        toggleLanguageMenu(event) {
            this.$refs.op_language.toggle(event);
        },
        checkAuthentication(){
            this.client('/api/user')
                .then(_ => {})
                .catch(_ => { window.location.href = this.config.public.api_endpoint + '/auth/redirect'});
        },
        selectLanguage(language) {
            this.viewLanguage = language;
            this.$refs.op_language.hide();

            // Emit event for components to refresh translations with temporary language
            if (import.meta.client) {
                const event = new CustomEvent('language-changed', { detail: this.viewLanguage });
                document.dispatchEvent(event);
            }
        },
        handleCurrentLanguageRequest() {
            // Respond to language request with current view language
            if (import.meta.client) {
                const event = new CustomEvent('language-changed', { detail: this.viewLanguage });
                document.dispatchEvent(event);
            }
        },
        getUrlLanguageParam() {
            if (import.meta.client) {
                const urlParams = new URLSearchParams(window.location.search);
                const lg = urlParams.get('lg')?.toUpperCase();
                if (lg && ['FR', 'EN', 'NL'].includes(lg)) {
                    return lg;
                }
            }
            return null;
        },
        // Override to use centralized translations with viewLanguage
        getStaticTranslation(key) {
            // For AppHeader, we use viewLanguage since it manages temporary language switching
            // Access centralized translations directly
            const { translations } = useTranslations();
            if (!translations) {
                console.error('Translations object is undefined');
                return key;
            }
            const langTranslations = translations[this.viewLanguage] || translations['NL'];
            if (!langTranslations) {
                console.error(`No translations found for language: ${this.viewLanguage}`);
                return key;
            }
            return langTranslations[key] || key;
        }
    },
    watch: {
        user: {
            immediate: true,
            handler(newUser) {
                if (newUser && newUser.language) {
                    this.currentLanguage = newUser.language;
                    // Initialize view language to user's default language
                    // But don't override if URL parameter is set
                    const urlLang = this.getUrlLanguageParam();
                    if (!urlLang && (!this.viewLanguage || this.viewLanguage === 'NL')) {
                        this.viewLanguage = newUser.language;
                    }
                }
            }
        }
    },
    mounted(){
        // Check URL parameter for language override
        const urlLang = this.getUrlLanguageParam();
        if (urlLang) {
            this.viewLanguage = urlLang;
            // Emit event for components to use URL language
            const event = new CustomEvent('language-changed', { detail: urlLang });
            document.dispatchEvent(event);
        }

        this.init();
        setInterval(() => this.checkAuthentication(), 5 * 1000 * 60);

        // Listen for language requests from other components
        if (import.meta.client) {
            document.addEventListener('request-current-language', this.handleCurrentLanguageRequest);
        }
    },
    beforeUnmount() {
        if (import.meta.client) {
            document.removeEventListener('request-current-language', this.handleCurrentLanguageRequest);
        }
    }
}
</script>

